import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import type { CoachInvite } from '@/hooks/useCoachInvites';

type Props = {
  invites: CoachInvite[];
  onAccept: (linkId: string) => Promise<{ ok: boolean }>;
  onDecline: (linkId: string) => Promise<{ ok: boolean }>;
};

export default function CoachInviteModal({ invites, onAccept, onDecline }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [processing, setProcessing] = useState<string | null>(null);

  if (invites.length === 0) return null;

  const invite = invites[0];

  const handleAccept = async () => {
    setProcessing('accept');
    try {
      const result = await onAccept(invite.id);
      if (result.ok) {
        Alert.alert('Connected', `You are now connected with ${invite.coachName}.`);
      } else {
        Alert.alert('Oops', "Couldn't accept the invite. Please try again.");
      }
    } catch {
      Alert.alert('Oops', "Something went wrong. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async () => {
    setProcessing('decline');
    try {
      await onDecline(invite.id);
    } catch {
      Alert.alert('Oops', "Something went wrong. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  return (
    <Modal visible transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.title, { color: c.text }]}>COACHING INVITE</Text>

          <Text style={[styles.body, { color: c.textSecondary }]}>
            <Text style={{ fontFamily: TYPO.body.bold, color: c.text }}>
              {invite.coachName}
            </Text>
            {invite.coachHandle ? (
              <Text style={{ color: c.textTertiary }}> @{invite.coachHandle}</Text>
            ) : null}
            {'\n'}wants to coach you.
          </Text>

          {invites.length > 1 && (
            <Text style={[styles.moreText, { color: c.textTertiary }]}>
              +{invites.length - 1} more invite{invites.length > 2 ? 's' : ''}
            </Text>
          )}

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.btn, styles.declineBtn, { borderColor: c.border }]}
              onPress={handleDecline}
              disabled={processing !== null}
              activeOpacity={0.7}
            >
              {processing === 'decline' ? (
                <ActivityIndicator size="small" color={c.textSecondary} />
              ) : (
                <Text style={[styles.btnText, { color: c.textSecondary }]}>Decline</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.btn, styles.acceptBtn, { backgroundColor: c.accent }]}
              onPress={handleAccept}
              disabled={processing !== null}
              activeOpacity={0.7}
            >
              {processing === 'accept' ? (
                <ActivityIndicator size="small" color={c.accentForeground} />
              ) : (
                <Text style={[styles.btnText, { color: c.accentForeground }]}>Accept</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 28,
  },
  card: {
    width: '100%',
    borderRadius: 2,
    borderWidth: 1,
    padding: 24,
    gap: 14,
  },
  title: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1.2,
    textAlign: 'center',
  },
  body: {
    fontSize: 15,
    fontFamily: TYPO.body.regular,
    lineHeight: 22,
    textAlign: 'center',
  },
  moreText: {
    fontSize: 12,
    fontFamily: TYPO.body.medium,
    textAlign: 'center',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 6,
  },
  btn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  declineBtn: {
    borderWidth: 1,
  },
  acceptBtn: {},
  btnText: {
    fontSize: 15,
    fontFamily: TYPO.body.bold,
  },
});
